import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Scale, AlertTriangle, PackageX, FileWarning } from "lucide-react";

export default function Balance() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [disc, setDisc] = useState(null);

  useEffect(() => {
    api.get("/branches").then(({ data }) => {
      setBranches(data);
      if (data.length) setBranchId((prev) => prev || data[0].id);
    });
    if (user && user.role !== "cabang") {
      api.get("/reports/discrepancies").then(({ data }) => setDisc(data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!branchId) return;
    api.get(`/reports/branch-balance?branch_id=${branchId}&date=${date}`).then(({ data }) => setReport(data));
  }, [branchId, date]);

  const totals = report ? report.rows.reduce((a, r) => ({
    masuk: a.masuk + r.masuk, terjual: a.terjual + r.terjual, selisih: a.selisih + r.selisih_terima,
  }), { masuk: 0, terjual: 0, selisih: 0 }) : null;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Monitoring Anti-Selisih</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Laporan Balance</h1>
        <p className="text-sm text-slate-600 mt-1">Bandingkan barang masuk vs terjual vs stok aktual per cabang per hari.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Cabang</span>
          <select data-testid="bal-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base">
            {branches.map(b => <option key={b.id} value={b.id}>{b.name} - {b.city}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Tanggal</span>
          <input data-testid="bal-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base" />
        </label>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-3">
          <div data-testid="bal-masuk" className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Masuk Hari Ini</p>
            <p className="mt-1 text-2xl font-extrabold text-emerald-700" style={{fontFamily:'Manrope'}}>+{totals.masuk}</p>
          </div>
          <div data-testid="bal-terjual" className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Terjual (Resi)</p>
            <p className="mt-1 text-2xl font-extrabold text-blue-700" style={{fontFamily:'Manrope'}}>-{totals.terjual}</p>
          </div>
          <div data-testid="bal-selisih" className={`rounded-xl border p-4 ${totals.selisih !== 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Selisih Terima</p>
            <p className={`mt-1 text-2xl font-extrabold ${totals.selisih !== 0 ? 'text-red-700' : 'text-slate-900'}`} style={{fontFamily:'Manrope'}}>{totals.selisih > 0 ? '+' : ''}{totals.selisih}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Produk</th>
              <th className="text-right px-4 py-3 font-bold">Masuk</th>
              <th className="text-right px-4 py-3 font-bold">Terjual</th>
              <th className="text-right px-4 py-3 font-bold">Selisih</th>
              <th className="text-right px-4 py-3 font-bold">Stok</th>
            </tr>
          </thead>
          <tbody>
            {(!report || report.rows.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Belum ada data untuk tanggal ini.</td></tr>
            )}
            {report?.rows.map((r) => (
              <tr key={r.product_id} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{r.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{r.sku}</p>
                </td>
                <td className="px-4 py-3 text-right text-emerald-700 font-semibold" style={{fontFamily:'IBM Plex Sans'}}>{r.masuk > 0 ? `+${r.masuk}` : r.masuk}</td>
                <td className="px-4 py-3 text-right text-blue-700 font-semibold" style={{fontFamily:'IBM Plex Sans'}}>{r.terjual > 0 ? `-${r.terjual}` : r.terjual}</td>
                <td className={`px-4 py-3 text-right font-bold ${r.selisih_terima !== 0 ? 'text-red-600' : 'text-slate-400'}`} style={{fontFamily:'IBM Plex Sans'}}>{r.selisih_terima > 0 ? `+${r.selisih_terima}` : r.selisih_terima}</td>
                <td className={`px-4 py-3 text-right font-bold ${r.stok < 0 ? 'text-red-600' : 'text-slate-900'}`} style={{fontFamily:'IBM Plex Sans'}}>{r.stok} <span className="text-xs text-slate-500 font-normal">{r.unit}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {disc && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{fontFamily:'Manrope'}}><Scale size={18} /> Ketidakseimbangan Terdeteksi</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div data-testid="disc-selisih-count" className="bg-white rounded-xl border-l-4 border-l-red-500 border-y border-r border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pengiriman Selisih</p>
              <p className="mt-1 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{disc.shipment_selisih.length}</p>
            </div>
            <div data-testid="disc-minus-count" className="bg-white rounded-xl border-l-4 border-l-red-500 border-y border-r border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Stok Minus</p>
              <p className="mt-1 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{disc.stok_minus.length}</p>
            </div>
            <div data-testid="disc-unconfirmed-count" className="bg-white rounded-xl border-l-4 border-l-amber-500 border-y border-r border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Resi Belum Konfirmasi</p>
              <p className="mt-1 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{disc.resi_belum_konfirmasi}</p>
            </div>
          </div>

          {disc.shipment_selisih.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1"><AlertTriangle size={14} className="text-red-500" /> Detail Pengiriman Selisih</p>
              {disc.shipment_selisih.map((s) => (
                <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-3">
                  {(s.received_photo_id || s.photo_id) && <img src={fileUrl(s.received_photo_id || s.photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider">Selisih {s.selisih > 0 ? '+' : ''}{s.selisih}</span>
                    <p className="mt-1 font-semibold text-slate-900">{s.product?.name || s.product_id}</p>
                    <p className="text-xs text-slate-600">Ke {s.branch?.name || s.to_branch_id} · dikirim {s.qty}, diterima {s.received_qty}</p>
                    <p className="text-xs text-slate-500">{new Date(s.created_at).toLocaleString('id-ID')} · oleh {s.created_by_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {disc.stok_minus.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1"><PackageX size={14} className="text-red-500" /> Stok Minus (terjual melebihi stok)</p>
              {disc.stok_minus.map((s) => (
                <div key={`${s.location}-${s.product_id}`} className="bg-red-50 rounded-xl border border-red-200 p-4">
                  <p className="font-semibold text-slate-900">{s.product?.name || s.product_id}</p>
                  <p className="text-xs text-slate-600">Lokasi: {s.location === 'gudang' ? 'Gudang Pusat' : (s.branch?.name || s.location)}</p>
                  <p className="text-sm font-bold text-red-700">Stok: {s.qty}</p>
                </div>
              ))}
            </div>
          )}

          {disc.shipment_selisih.length === 0 && disc.stok_minus.length === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3">
              <FileWarning size={20} className="text-emerald-600" />
              <p className="text-sm font-semibold text-emerald-800">Semua data balance — tidak ada selisih atau stok minus.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
